import { useState, useMemo } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateEmergencyFund } from '../../lib/calculations/emergencyFund'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

function InputField({ label, value, onChange, prefix, suffix, type = 'number', hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type={type}
          value={value}
          onChange={e => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-8' : 'pr-3'}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function SelectField({ label, value, onChange, options }) {
  const { state } = useFinance()
  const colors = getThemeColors(state.theme)
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-2 rounded-xl text-xs font-medium transition-all ${
              value === opt.value ? 'text-heading shadow-sm' : 'text-muted hover:text-heading'
            }`}
            style={value === opt.value ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}44`,
            } : { border: '1px solid transparent' }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function EmergencyFund() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)

  const [monthlyExpenses, setMonthlyExpenses] = useState(4000)
  const [age, setAge] = useState(30)
  const [employmentType, setEmploymentType] = useState('w2_stable')
  const [incomeSources, setIncomeSources] = useState('single')
  const [dependents, setDependents] = useState('none')
  const [housing, setHousing] = useState('rent')
  const [health, setHealth] = useState('good')
  const [jobMarket, setJobMarket] = useState('moderate')
  const [currentSavings, setCurrentSavings] = useState(5000)
  const [monthlyContribution, setMonthlyContribution] = useState(500)
  const [savingsAPY, setSavingsAPY] = useState(4.5)

  const result = useMemo(() => calculateEmergencyFund({
    monthlyExpenses, age, employmentType, incomeSources, dependents,
    housing, health, jobMarket, currentSavings, monthlyContribution, savingsAPY,
  }), [monthlyExpenses, age, employmentType, incomeSources, dependents, housing, health, jobMarket, currentSavings, monthlyContribution, savingsAPY])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    const ensureSpace = (needed) => {
      if ((doc.lastAutoTable?.finalY || margin) + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return (doc.lastAutoTable?.finalY || margin) + 10
    }

    const addSectionTitle = (title, y) => {
      doc.setFontSize(13)
      doc.setTextColor(40, 40, 40)
      doc.text(title, margin, y)
      return y + 6
    }

    // ── Header ──
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')
    doc.setFontSize(22)
    doc.setTextColor(30, 30, 30)
    doc.text('Emergency Fund Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)
    doc.setDrawColor(220, 220, 220)
    doc.line(margin, 33, pageWidth - margin, 33)

    // ── Section 1: Your Situation (Financial Inputs) ──
    let y = 40
    y = addSectionTitle('Your Situation', y)

    const employmentLabels = { w2_stable: 'W-2 Stable', w2_variable: 'W-2 Variable', self_employed: 'Self-Employed', retired: 'Retired' }
    const incomeLabels = { single: 'Single Income', dual: 'Dual Income' }
    const dependentLabels = { none: 'None', kids: 'Children', aging_parents: 'Aging Parents', both: 'Both' }
    const housingLabels = { rent: 'Renting', own: 'Homeowner' }
    const healthLabels = { good: 'Good', fair: 'Fair', chronic: 'Chronic Condition' }
    const jobMarketLabels = { easy: 'Easy to Find Work', moderate: 'Moderate', hard: 'Hard to Find Work' }

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: [
        ['Age', String(age)],
        ['Monthly Expenses', formatCurrency(monthlyExpenses)],
        ['Current Savings', formatCurrency(currentSavings)],
        ['Monthly Contribution', formatCurrency(monthlyContribution)],
        ['HYSA APY', `${savingsAPY}%`],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 2: Risk Factors ──
    y = ensureSpace(60)
    y = addSectionTitle('Risk Factors', y)

    autoTable(doc, {
      head: [['Factor', 'Selection']],
      body: [
        ['Employment Type', employmentLabels[employmentType] || employmentType],
        ['Income Sources', incomeLabels[incomeSources] || incomeSources],
        ['Dependents', dependentLabels[dependents] || dependents],
        ['Housing', housingLabels[housing] || housing],
        ['Health Status', healthLabels[health] || health],
        ['Job Market', jobMarketLabels[jobMarket] || jobMarket],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 3: Key Results ──
    y = ensureSpace(60)
    y = addSectionTitle('Key Results', y)

    const timeToFunded = result.gap <= 0
      ? 'Fully Funded!'
      : result.monthsToFunded >= 12
        ? `${Math.floor(result.monthsToFunded / 12)}y ${result.monthsToFunded % 12}m`
        : `${result.monthsToFunded} months`

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Recommended Months', `${result.recommendedMonths} months`],
        ['Target Amount', formatCurrency(result.targetAmount)],
        ['Current Savings', formatCurrency(result.currentSavings)],
        ['Gap to Fill', formatCurrency(result.gap)],
        ['Time to Fully Funded', timeToFunded],
      ],
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // ── Section 4: Progress ──
    y = ensureSpace(40)
    y = addSectionTitle('Progress', y)

    const progressData = [
      ['Current Progress', `${result.progress}%`],
      ['Saved', formatCurrency(result.currentSavings)],
      ['Target', formatCurrency(result.targetAmount)],
    ]
    if (result.gap > 0 && result.monthlyContribution > 0) {
      progressData.push(['Saving Rate', `${formatCurrency(result.monthlyContribution)}/mo at ${savingsAPY}% APY`])
      if (result.interestEarned > 0) {
        progressData.push(['Interest Earned (while saving)', formatCurrency(result.interestEarned)])
      }
    }

    autoTable(doc, {
      head: [['Detail', 'Value']],
      body: progressData,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    })

    // Draw a progress bar in the PDF
    y = (doc.lastAutoTable?.finalY || y) + 6
    if (y + 14 > pageHeight - margin) { doc.addPage(); y = margin + 4 }
    const barWidth = pageWidth - margin * 2
    const barHeight = 8
    doc.setFillColor(230, 230, 230)
    doc.roundedRect(margin, y, barWidth, barHeight, 3, 3, 'F')
    const fillWidth = Math.min(100, result.progress) / 100 * barWidth
    if (result.progress >= 100) {
      doc.setFillColor(34, 197, 94)
    } else {
      doc.setFillColor(59, 130, 246)
    }
    if (fillWidth > 0) doc.roundedRect(margin, y, fillWidth, barHeight, 3, 3, 'F')
    doc.setFontSize(8)
    doc.setTextColor(80, 80, 80)
    doc.text(`${result.progress}%`, margin + barWidth / 2 - 5, y + barHeight + 8)

    // ── Section 5: Score Breakdown ──
    y = y + barHeight + 16
    if (y + 60 > pageHeight - margin) { doc.addPage(); y = margin + 4 }
    y = addSectionTitle('Score Breakdown', y)

    const scoreRows = result.scoreBreakdown.map(item => [
      item.factor,
      item.value,
      `+${item.score}`,
    ])
    scoreRows.push([
      { content: `Total (base 3 + ${result.totalScore})`, styles: { fontStyle: 'bold' } },
      '',
      { content: `${result.recommendedMonths} months`, styles: { fontStyle: 'bold' } },
    ])

    autoTable(doc, {
      head: [['Risk Factor', 'Your Selection', 'Score']],
      body: scoreRows,
      startY: y,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [245, 158, 11], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [255, 251, 235] },
    })

    // ── Section 6: Expert Recommendations ──
    y = ensureSpace(70)
    y = addSectionTitle('Expert Recommendations', y)

    const compRows = result.sourceComparisons.map(comp => [
      comp.source,
      `${comp.months} mo`,
      comp.description,
    ])

    autoTable(doc, {
      head: [['Source', 'Recommendation', 'Notes']],
      body: compRows,
      startY: y,
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 4 },
      headStyles: { fillColor: [107, 114, 128], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      columnStyles: { 2: { cellWidth: 80 } },
    })

    // ── Footer on every page ──
    const totalPages = doc.getNumberOfPages()
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i)
      doc.setFillColor(255, 255, 255)
      doc.setFontSize(8)
      doc.setTextColor(180, 180, 180)
      doc.text(`Liquid Finance  |  Emergency Fund Report  |  Page ${i} of ${totalPages}`, margin, pageHeight - 8)
    }

    doc.save(`emergency-fund-report-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Financial Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-heading">Your Situation</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Age" value={age} onChange={setAge} hint="Older = longer job searches & higher health costs" />
          <InputField label="Monthly Expenses" value={monthlyExpenses} onChange={setMonthlyExpenses} prefix="$" />
          <InputField label="Current Savings" value={currentSavings} onChange={setCurrentSavings} prefix="$" />
          <InputField label="Monthly Contribution" value={monthlyContribution} onChange={setMonthlyContribution} prefix="$" hint="How much you can save per month" />
          <InputField label="HYSA APY" value={savingsAPY} onChange={setSavingsAPY} suffix="%" hint="High-yield savings account rate" />
        </div>
      </GlassCard>

      {/* Risk Factors */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Risk Factors</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <SelectField label="Employment Type" value={employmentType} onChange={setEmploymentType} options={[
            { value: 'w2_stable', label: 'W-2 Stable' },
            { value: 'w2_variable', label: 'W-2 Variable' },
            { value: 'self_employed', label: 'Self-Employed' },
            { value: 'retired', label: 'Retired' },
          ]} />
          <SelectField label="Income Sources" value={incomeSources} onChange={setIncomeSources} options={[
            { value: 'single', label: 'Single Income' },
            { value: 'dual', label: 'Dual Income' },
          ]} />
          <SelectField label="Dependents" value={dependents} onChange={setDependents} options={[
            { value: 'none', label: 'None' },
            { value: 'kids', label: 'Children' },
            { value: 'aging_parents', label: 'Aging Parents' },
            { value: 'both', label: 'Both' },
          ]} />
          <SelectField label="Housing" value={housing} onChange={setHousing} options={[
            { value: 'rent', label: 'Renting' },
            { value: 'own', label: 'Homeowner' },
          ]} />
          <SelectField label="Health Status" value={health} onChange={setHealth} options={[
            { value: 'good', label: 'Good' },
            { value: 'fair', label: 'Fair' },
            { value: 'chronic', label: 'Chronic Condition' },
          ]} />
          <SelectField label="Job Market" value={jobMarket} onChange={setJobMarket} options={[
            { value: 'easy', label: 'Easy to Find Work' },
            { value: 'moderate', label: 'Moderate' },
            { value: 'hard', label: 'Hard to Find Work' },
          ]} />
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard label="Recommended Months" value={`${result.recommendedMonths} months`} color={colors.primary} highlight />
        <ResultCard label="Target Amount" value={formatCurrency(result.targetAmount)} color={colors.secondary} highlight />
        <ResultCard label="Gap to Fill" value={formatCurrency(result.gap)} color={result.gap > 0 ? colors.warning : colors.positive} />
        <ResultCard
          label="Time to Fully Funded"
          value={result.gap <= 0 ? 'Fully Funded!' : result.monthsToFunded >= 12 ? `${Math.floor(result.monthsToFunded / 12)}y ${result.monthsToFunded % 12}m` : `${result.monthsToFunded} months`}
          color={result.gap <= 0 ? colors.positive : colors.primary}
        />
      </div>

      {/* Progress Bar */}
      <GlassCard>
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-semibold text-heading">Progress</h3>
          <span className="text-sm font-medium" style={{ color: result.progress >= 100 ? colors.positive : colors.primary }}>
            {result.progress}%
          </span>
        </div>
        <div className="w-full h-4 rounded-full bg-subtle overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(100, result.progress)}%`,
              background: result.progress >= 100
                ? colors.positive
                : `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted mt-2">
          <span>{formatCurrency(result.currentSavings)} saved</span>
          <span>{formatCurrency(result.targetAmount)} target</span>
        </div>
        {result.gap > 0 && result.monthlyContribution > 0 && (
          <div className="flex justify-between text-xs text-muted mt-1 pt-1 border-t border-card">
            <span>Saving {formatCurrency(result.monthlyContribution)}/mo at {savingsAPY}% APY</span>
            <span>{result.interestEarned > 0 ? `+${formatCurrency(result.interestEarned)} interest earned` : ''}</span>
          </div>
        )}
      </GlassCard>

      {/* Score Breakdown */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Score Breakdown</h3>
        <div className="space-y-2">
          {result.scoreBreakdown.map(item => (
            <div key={item.factor} className="flex items-center justify-between text-sm">
              <span className="text-muted">{item.factor}</span>
              <div className="flex items-center gap-3">
                <span className="text-heading capitalize">{item.value}</span>
                <span className="w-8 text-right font-mono text-xs" style={{ color: item.score > 0 ? colors.warning : colors.positive }}>
                  +{item.score}
                </span>
              </div>
            </div>
          ))}
          <div className="border-t border-card pt-2 flex justify-between text-sm font-semibold">
            <span className="text-heading">Total Score (base 3 + {result.totalScore})</span>
            <span style={{ color: colors.primary }}>{result.recommendedMonths} months</span>
          </div>
        </div>
      </GlassCard>

      {/* Source Comparisons */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Expert Recommendations</h3>
        <div className="space-y-3">
          {result.sourceComparisons.map(comp => (
            <div key={comp.source} className="flex items-center justify-between p-3 rounded-xl bg-subtle">
              <div>
                <p className="text-sm font-medium text-heading">{comp.source}</p>
                <p className="text-xs text-muted">{comp.description}</p>
              </div>
              <span className="text-sm font-bold text-heading whitespace-nowrap ml-4">{comp.months} mo</span>
            </div>
          ))}
        </div>
      </GlassCard>
    </div>
  )
}
